import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { 
  Sparkles, Scissors, Flame, Gift, Crown, Heart, PlusCircle, 
  ChevronRight, Loader2, Calendar, AlertCircle, Star, Check
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICON_MAP = {
  sparkles: Sparkles,
  scissors: Scissors,
  flame: Flame,
  gift: Gift,
  crown: Crown,
  heart: Heart,
  "plus-circle": PlusCircle,
};

// Deposit Section Component
const DepositSection = () => {
  const [isLoading, setIsLoading] = useState(false);

  const handleDepositCheckout = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/checkout/session`, {
        service_id: "booking-deposit",
        origin_url: window.location.origin
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error("Unable to process deposit. Please try again.");
      setIsLoading(false);
    }
  };

  return (
    <motion.div 
      className="max-w-3xl mx-auto mb-12"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
    >
      <div className="aurora-border rounded-2xl p-1">
        <div className="bg-black/60 rounded-xl p-6 md:p-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-full bg-gradient-to-r from-emerald-500/20 to-cyan-500/20">
                <Calendar className="text-cyan-400" size={28} />
              </div>
              <div>
                <h3 className="font-italiana text-2xl text-white mb-2">Reserve Your Appointment</h3>
                <p className="text-white/70 text-sm mb-3">
                  Secure your spot with a <span className="text-cyan-400 font-semibold">$50 deposit</span>
                </p>
                <div className="flex items-start gap-2 text-white/50 text-xs">
                  <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                  <p>If you do not reschedule at least 48 hours before your appointment, the deposit is non-refundable.</p>
                </div>
              </div>
            </div>
            <Button 
              onClick={handleDepositCheckout}
              disabled={isLoading}
              className="btn-primary rounded-full px-8 py-6 whitespace-nowrap"
              data-testid="deposit-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  Pay $50 Deposit <ChevronRight className="ml-2" size={18} />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Membership Section Component
const MembershipSection = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubscribe = async () => {
    if (!email || !name) {
      toast.error("Please enter your name and email");
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/subscribe`, {
        origin_url: window.location.origin,
        email,
        name
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error("Unable to process subscription. Please try again.");
      setIsLoading(false);
    }
  };

  return (
    <>
      <motion.div 
        className="max-w-4xl mx-auto mb-16"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="relative overflow-hidden rounded-2xl">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 via-cyan-500/20 to-emerald-500/20" />
          <div className="relative glass-card rounded-2xl p-8 md:p-10 border border-purple-500/30">
            <div className="flex items-center gap-2 mb-4">
              <Star className="text-yellow-400" size={24} />
              <span className="text-yellow-400 text-sm font-semibold uppercase tracking-wider">Exclusive Membership</span>
            </div>
            
            <h3 className="font-italiana text-3xl md:text-4xl text-white mb-2">Glamour Glow Membership</h3>
            <div className="flex items-baseline gap-2 mb-6">
              <span className="text-4xl font-syne gradient-text">$100</span>
              <span className="text-white/60">/month</span>
              <span className="text-white/40 text-sm ml-2">• 6-month commitment</span>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div>
                <h4 className="text-white font-semibold mb-3">Includes:</h4>
                <ul className="space-y-2">
                  {[
                    "1 Monthly Service Credit (up to $125)",
                    "Combine smaller services up to $125",
                    "10% off upgrades on services above $125",
                    "20% off additional services each month",
                    "Priority booking",
                    "Birthday add-on"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-white/70 text-sm">
                      <Check size={16} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h4 className="text-white font-semibold mb-3">Credit Works Great For:</h4>
                <ul className="space-y-2 mb-6">
                  {[
                    "Signature Glam Facial",
                    "Wood Therapy Sculpt",
                    "Post-Op Drainage",
                    "Brazilian + Add-ons",
                    "Full Face Wax combos"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-white/70 text-sm">
                      <Check size={16} className="text-cyan-400 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                
                <p className="text-white/40 text-xs">
                  Not covered: VIP Packages, Body Contouring Bundles, Bridal Radiance
                </p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => setIsOpen(true)}
                className="btn-primary rounded-full px-8 py-6 text-lg"
                data-testid="membership-btn"
              >
                Join Membership <Star className="ml-2" size={18} />
              </Button>
              <p className="text-white/50 text-xs self-center">
                Credits expire after 60 days • Non-transferable • No-show redeems credit
              </p>
            </div>
          </div>
        </div>
      </motion.div>
      
      {/* Membership Signup Modal */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="glass-card border-white/20 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-italiana text-2xl text-white">Join Glamour Glow</DialogTitle>
            <DialogDescription className="text-white/60">
              $100/month • 6-month commitment
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="block text-white/70 text-sm mb-2">Your Name</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input-dark rounded-lg"
                placeholder="Full name"
              />
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-dark rounded-lg"
                placeholder="your@email.com"
              />
            </div>
            
            <Button 
              onClick={handleSubscribe}
              disabled={isLoading}
              className="w-full btn-primary rounded-full py-6"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  Subscribe Now <ChevronRight className="ml-2" size={18} />
                </>
              )}
            </Button>
            
            <p className="text-white/40 text-xs text-center">
              You'll be redirected to Stripe to complete your subscription
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const ServiceCard = ({ service, onBook }) => {
  return (
    <motion.div
      whileHover={{ y: -4 }}
      className="service-card glass-card rounded-2xl p-5 cursor-pointer"
      data-testid={`service-card-${service.id}`}
    >
      <div className="flex justify-between items-start mb-3">
        <h3 className="text-white font-semibold text-base">{service.name}</h3>
        <span className="price-tag px-3 py-1 rounded-full text-sm font-syne text-emerald-300">
          ${service.price.toFixed(0)}
        </span>
      </div>
      <p className="text-white/50 text-sm mb-4 line-clamp-2">{service.description}</p>
      <Button 
        onClick={() => onBook(service)} 
        className="w-full btn-secondary rounded-full text-sm py-2"
        data-testid={`book-btn-${service.id}`}
      >
        Book Now <ChevronRight size={16} className="ml-1" />
      </Button>
    </motion.div>
  );
};

const BookingModal = ({ service, isOpen, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleCheckout = async () => {
    if (!service) return;
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/checkout/session`, {
        service_id: service.id,
        origin_url: window.location.origin
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error("Unable to process checkout. Please try again.");
      setIsLoading(false);
    }
  };

  if (!service) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="glass-card border-white/20 max-w-md" data-testid="booking-modal">
        <DialogHeader>
          <DialogTitle className="font-italiana text-2xl text-white">{service.name}</DialogTitle>
          <DialogDescription className="text-white/60">
            Book this service and proceed to secure payment
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <p className="text-white/70">{service.description}</p>
          
          <div className="flex items-center justify-between py-4 border-y border-white/10">
            <span className="text-white/70">Price</span>
            <span className="font-syne text-2xl gradient-text">${service.price.toFixed(2)}</span>
          </div>
          
          <div className="space-y-3">
            <Button 
              onClick={handleCheckout}
              disabled={isLoading}
              className="w-full btn-primary rounded-full py-6"
              data-testid="checkout-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  Proceed to Payment <ChevronRight className="ml-2" size={18} />
                </>
              )}
            </Button>
            
            <Button 
              onClick={onClose}
              variant="outline"
              className="w-full btn-secondary rounded-full"
              data-testid="cancel-btn"
            >
              Cancel
            </Button>
          </div>
          
          <p className="text-white/40 text-xs text-center">
            Consultation required before first body contouring session.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ServicesPage = () => {
  const [services, setServices] = useState({});
  const [selectedService, setSelectedService] = useState(null);
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const categoryOrder = ["facials", "addons", "waxing", "body_contouring", "vip_packages"];

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error("Error fetching services:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const handleBook = (service) => {
    setSelectedService(service);
    setIsBookingOpen(true);
  };

  return (
    <>
      <SEO 
        title="Services & Pricing"
        description="Explore our full menu of beauty services including advanced facials ($125-$175), body contouring ($110-$165), waxing ($15-$90), and VIP packages. Join our Glamour Glow Membership for exclusive savings!"
        keywords="facial treatments Katy TX, body contouring prices Houston, Brazilian wax Katy, hydro jelly facial, wood therapy Houston, cavitation treatment, body sculpting packages, VIP beauty packages, beauty membership Katy Texas"
        canonical="https://glamourbodyhouston.com/services"
      />
      
      <div className="pt-24 min-h-screen">
        <section className="relative py-16 px-4" data-testid="services-section">
          <div className="orb-green top-20 -left-40" />
          <div className="orb-purple top-60 -right-60" />
          <div className="orb-teal bottom-40 left-20" />
          <div className="orb-cyan bottom-20 -right-40" />
          
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div 
              className="text-center mb-12"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h1 className="font-italiana text-4xl sm:text-5xl text-white mb-4 tracking-wider uppercase">Our Services</h1>
              <p className="text-white/60 text-base lg:text-lg max-w-2xl mx-auto">Premium beauty and body treatments tailored to your unique needs</p>
            </motion.div>
            
            {/* Deposit Section */}
            <DepositSection />
            
            {/* Membership Section */}
            <MembershipSection />
            
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <Loader2 className="h-8 w-8 text-cyan-400 animate-spin" />
              </div>
            ) : (
              categoryOrder.map((categoryKey, categoryIndex) => {
                const category = services[categoryKey];
                if (!category) return null;
                
                const IconComponent = ICON_MAP[category.icon] || Sparkles;
                
                return (
                  <motion.div 
                    key={categoryKey}
                    className="mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: categoryIndex * 0.1 }}
                  >
                    <div className="flex items-center gap-3 mb-6">
                      <IconComponent className="text-emerald-400" size={24} />
                      <h2 className="font-italiana text-2xl text-white tracking-wide">{category.category}</h2>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {category.items.map((service, index) => (
                        <motion.div
                          key={service.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <ServiceCard service={service} onBook={handleBook} />
                        </motion.div>
                      ))}
                    </div>
                    
                    <div className="section-divider mt-12" />
                  </motion.div>
                );
              })
            )}
          </div>
        </section>
        
        <BookingModal 
          service={selectedService} 
          isOpen={isBookingOpen} 
          onClose={() => setIsBookingOpen(false)} 
        />
      </div>
    </>
  );
};

export default ServicesPage;
